<template>
  <div>
    <b-modal v-model="confirmRemoveModal"
      title="Remover Arquivo"
      class="modal-primary"
      @ok="removeFile">

      Deseja remover o arquivo do item do Processo? Ao confirmar essa ação o item não poderá ser recuperado.
      <br>
      Caso deseje substituir o arquivo recomendamos que inative esse item e crie um novo.
    </b-modal>

    <b-card title="Item do Processo" header-tag="header" footer-tag="footer">
      <div slot="header" class="row mb-0 align-items-right">
        <div class="col-12 text-right ">
          <div class="switch-container">
            <span class="switch-label mr-1">
              Status
            </span>

            <toggle-button class="mb-0"
              v-model="form.status"
              :width="70"
              :height="25"
              :labels="{checked: 'Ativo', unchecked: 'Inativo'}"/>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="row">
            <b-form-group
              class="col-12 col-md-6"
              label="Processo"
              label-for="process_id">

              <multiselect v-model="selectedProcess"
                @select="updateForm('process_id', arguments[0])"
                placeholder="Selecione o Processo"
                :allowEmpty="true"
                :options="processes"
                label="company_name"
                :showLabels="false"
                track-by="id"
                :class="{
                  'is-invalid': submitted && form.errors.has('process_id'),
                  'is-valid': submitted && !form.errors.has('process_id')
                }"/>
            </b-form-group>

            <b-form-group class="col-12 col-md-6"
              label="Categoria"
              label-for="category_id">
              <multiselect
                v-model="selectedCategory"
                @select="updateForm('category_id', arguments[0])"
                :disabled="selectedProcess === null"
                :placeholder="selectedProcess
                  ? 'Selecione a categoria'
                  : 'Selecione primeiro o processo'"
                :allowEmpty="true"
                :options="categories"
                label="name"
                :showLabels="false"
                track-by="id"
                :class="{
                  'is-invalid': submitted && form.errors.has('category_id'),
                  'is-valid': submitted && !form.errors.has('category_id')
                }"/>
            </b-form-group>
          </div>

          <div class="row">
            <b-form-group
              :class="'col-12 col-sm-6'"
              label="* Nome"
              label-for="name">

              <b-form-input
                id="name"
                v-model="form.name"
                placeholder="Nome"
                required
                :class="{
                  'is-invalid': submitted && form.errors.has('name'),
                  'is-valid': submitted && !form.errors.has('name')
                }"
                @input="slugify">
              </b-form-input>
            </b-form-group>

            <b-form-group class="col-12 col-sm-6"
              label="Slug"
              label-for="slug"
              description="(Se vazia será gerada uma slug automática)">

              <b-form-input
                id="slug"
                v-model="form.slug"
                placeholder="slug"
                :class="{
                  'is-invalid': submitted && form.errors.has('slug'),
                  'is-valid': submitted && !form.errors.has('slug')
                }"
                @change="userEditedSlug = true">
              </b-form-input>
            </b-form-group>
          </div>
        </div>
      </div>

      <div class="row" v-if="selectedCategory !== null">
        <div class="col-12">
          <hr>
        </div>

        <!-- Image Upload -->
        <b-form-group v-if="selectedCategory.type_id === 1"
          class="col-12 col-md-4"
          label="Imagem"
          label-for="media">

          <single-media-uploader
            name="file"
            placeholder="static/img/defaults/upload.png"
            @success="handleUploadMedia"
            :url="mediaUrl"
            :source="mediaInstance">
          </single-media-uploader>
        </b-form-group>

        <!-- Video Source -->
        <b-form-group v-if="selectedCategory.type_id === 2 || selectedCategory.type_id === 4"
          :class="'col-12'"
          label="Fonte"
          label-for="source">

          <b-form-input
            id="source"
            v-model="form.source"
            placeholder="Fonte (URL)"
            :class="{
              'is-invalid': submitted && form.errors.has('source'),
              'is-valid': submitted && !form.errors.has('source')
            }">
          </b-form-input>
        </b-form-group>

        <!-- File Upload -->
        <b-form-group v-if="selectedCategory.type_id === 4"
          class="col-12"
          label="Anexo"
          label-for="file">

          <single-file-input id="file"
            @error="$toasted.error(arguments[1])"
            @success="handleUploadFile"
            @remove-file="handleRemoveFile"
            :value="fileInstance"
            :url="uploadUrl">
          </single-file-input>
        </b-form-group>
      </div>
      <slot name="footer"></slot>
    </b-card>
  </div>
</template>

<script>
import { get, set, isEmpty } from 'lodash'
import slugify from 'slugify'
import { resolveHTTP } from '@/services/api'

export default {
  props: {
    form: {
      type: [Object],
      required: true
    },

    submitted: {
      type: Boolean,
      default: false
    },

    instance: {
      type: Object,
      default () {
        return {
          media: [],
          file: {}
        }
      }
    },

    http: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      userEditedSlug: false,
      confirmRemoveModal: false,

      locks: {
        processes: true,
        categories: true
      },

      media: {},
      file: {},

      selectedProcess: null,
      processes: [],

      selectedCategory: null,
      // categories: [],

      empty: {
        _attach_files: [],
        name: '',
        source: '',
        slug: '',
        category_id: null,
        process_id: null,
        media_ids: [],
        file: null,
        status: true
      }
    }
  },

  watch: {
    '$props': {
      handler: function (newProps) {
        if (!get(newProps, 'form.process_id', false)) {
          this.selectedProcess = null
        }

        if (!get(newProps, 'form.category_id', false)) {
          this.selectedCategory = null
        }

        if (isEmpty(!get(newProps, 'form._attach_files', false))) {
          this.clearFileInput()
        }
      },
      deep: true
    }
  },

  computed: {
    ready () {
      return !(this.locks.processes || this.locks.categories)
    },

    categories () {
      if (this.selectedProcess !== null) {
        return get(this.selectedProcess, 'categories', [])
      }

      return []
    },

    categoryType () {
      if (this.selectedCategory !== null) {
        return get(this.selectedCategory, 'type_id', [])
      }

      return []
    },

    uploadUrl () {
      return this.http.routes.uploadFile(this.instance.id)
    },

    fileInstance () {
      let file = {}

      if (!isEmpty(this.file)) {
        file = this.file
      }

      if (!isEmpty(this.instance.file)) {
        file = this.instance.file
      }

      if (!isEmpty(this._links)) {
        file['_links'] = this._links
      }

      if (!isEmpty(this.instance._links)) {
        file['_links'] = this.instance._links
      }

      return file
    },

    mediaUrl () {
      return this.http.routes.uploadMedia(this.instance.id)
    },

    mediaInstance () {
      if (!isEmpty(this.media)) {
        return this.media
      }
      if (!isEmpty(this.instance.media)) {
        return this.instance.media[0]
      }
      return {}
    }
  },
  methods: {
    get,

    handleUploadMedia (file, response, progress) {
      this.form.media_ids = [response.id]
      this.media = response
    },

    handleUploadFile (file, response, progress) {
      if (this.form._attach_files === undefined) {
        this.form._attach_files = this.form.initial._attach_files || []
      }
      this.form._attach_files.push(response)
      this.file = response
    },

    handleRemoveFile (file) {
      if (this.instance.id) {
        this.confirmRemoveModal = true
        return null
      }

      this.clearFileInput()
    },

    removeFile () {
      const self = this
      this.$Progress.start()
      self.confirmRemoveModal = false
      self.http.destroyFile(self.instance.id, self.fileInstance.id)
        .then((res) => {
          self.clearFileInput()
          self.$toasted.success('Arquivo removido com sucesso')
          self.$emit('refresh')
        })
    },

    clearFileInput () {
      this.form._attach_files = []
      this.file = {}
    },

    updateForm (path, value) {
      set(this.form, path, value ? value.id : null)
    },

    slugify () {
      if (this.userEditedSlug) {
        return
      }

      this.form.slug = slugify(this.form.name)
    }
  },

  created () {
    const self = this

    resolveHTTP('/bl/processes').index({ params: { _onepage: true, _with: 'categories' } })
      .then(function (res) {
        self.processes = res.data.collection
        self.locks.processes = false

        self.selectedProcess = self.form.process_id
          ? self.processes.find((o) => o.id === self.form.process_id)
          : null

        self.selectedCategory = self.form.category_id
          ? self.categories.find((o) => o.id === self.form.category_id)
          : null
      })

    if (self.instance.id) {
      self.form.setInitialValues(self.instance)

      self.form.process_id = self.instance.process_id || null
      self.form.category_id = self.instance.category_id || null
    } else {
      self.form.withData(self.empty).setInitialValues(this.empty)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/forms/Bl/ProcessItem.vue